import { Suspense, useEffect, useState } from 'react';
import { lazyWithReload } from './lib/lazyWithReload';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dashboard } from './pages/Dashboard';
import { MapPage } from './pages/Map';
import { Reports } from './pages/Reports';
import { DarkModeProvider } from './hooks/DarkModeProvider';
import { PostHogProvider } from './components/providers/PostHogProvider';
import { AuthProvider } from './contexts/AuthProvider';
import { useAuth } from './contexts/AuthContext';
import { RBACProvider } from './contexts/RBACProvider';
import { useRBAC } from './contexts/RBACContext';
import { SuperadminNotificationProvider } from './contexts/SuperadminNotificationProvider';
import { FilterProvider } from './contexts/FilterProvider';
import { MapProvider } from './contexts/MapProvider';
import { UnitStatusProvider } from './contexts/UnitStatusProvider';
import { LiveDemoProvider } from './contexts/LiveDemoProvider';
import { UserLocationProvider } from './contexts/UserLocationProvider';
import { SettingsProvider } from './contexts/SettingsProvider';
import { useSystemConfig } from './contexts/SettingsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { InternalRoute } from './components/auth/InternalRoute';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import { SettingsLayout } from './components/layout/SettingsLayout';
import { RecordsLayout } from './components/layout/RecordsLayout';
import { SettingsOverview } from './pages/settings/Overview';
import { SettingsProfile } from './pages/settings/Profile';
import { SettingsNotifications } from './pages/settings/Notifications';
// Messages and Invoices - TBD, hidden for now
// import { SettingsMessages } from './pages/settings/Messages';
// import { SettingsInvoices } from './pages/settings/Invoices';
import { SettingsGroups } from './pages/settings/Groups';
import { SettingsUnits } from './pages/settings/Units';
import { SettingsAuditLogs } from './pages/settings/AuditLogs';
import { SettingsCompany } from './pages/settings/Company';
import { AdminCompanies } from './pages/settings/Companies';
import { AdminUsers } from './pages/settings/Users';
import { SettingsConfig } from './pages/settings/Config';
import { SettingsDocumentShares } from './pages/settings/DocumentShares';
import { AlertsSettings } from './pages/settings/AlertsSettings';
import { TrainingSettings } from './pages/settings/TrainingSettings';
import { AiSettings } from './pages/settings/AiSettings';
import { ClientPortalSettings } from './pages/settings/ClientPortalSettings';
import { ReportSubscriptionsInventory } from './pages/settings/ReportSubscriptionsInventory';
import { UnbilledDeployments } from './pages/settings/UnbilledDeployments';
import { SettingsFilters } from './pages/settings/Filters';
import { SettingsGlobalAudit } from './pages/settings/GlobalAudit';
import { ElectricRates } from './pages/settings/ElectricRates';
import { DocumentTypes } from './pages/settings/DocumentTypes';
// Settings CheckIns removed — all check-in management now lives in /records/check-ins
import { SettingsQRCodes } from './pages/settings/QRCodes';
import { SettingsPocket } from './pages/settings/PocketSettings';
import { SystemDevices } from './pages/settings/SystemDevices';
import { TrainingLayout } from './components/layout/TrainingLayout';
import { Home as TrainingHome } from './pages/training/screens/Home';
import { Roadmap as TrainingRoadmap } from './pages/training/screens/Roadmap';
import { Lessons as TrainingLessons } from './pages/training/screens/Lessons';
import { LessonDetail as TrainingLessonDetail } from './pages/training/screens/LessonDetail';
import { Flashcards as TrainingFlashcards } from './pages/training/screens/Flashcards';
import { Practice as TrainingPractice } from './pages/training/screens/Practice';
import { Exam as TrainingExam } from './pages/training/screens/Exam';
import { Review as TrainingReview } from './pages/training/screens/Review';
import './styles/globals.css';
import { useFilters } from './contexts/FilterContext';
import { LottieLoading } from './components/ui/lottie-loading';

const GalleryPage = lazyWithReload(() => import('./pages/GalleryPage').then(module => ({ default: module.GalleryPage })));
const CheckInOutPage = lazyWithReload(() => import('./pages/CheckInOut'));
// T-144 PR#3b: public document-share capability page — no auth gate.
const DocumentSharePage = lazyWithReload(() => import('./pages/DocumentShare'));
// T-036: Universal Link landing — public, no auth gate.
const HirePage = lazyWithReload(() => import('./pages/hire/HirePage'));
const PortalAccountPage = lazyWithReload(() => import('./pages/portal/PortalPages').then(module => ({ default: module.PortalAccountPage })));
const PortalSharedPage = lazyWithReload(() => import('./pages/portal/PortalPages').then(module => ({ default: module.PortalSharedPage })));
const PortalLinkLanding = lazyWithReload(() => import('./pages/portal/PortalPages').then(module => ({ default: module.PortalLinkLanding })));
const PortalVerifyLanding = lazyWithReload(() => import('./pages/portal/PortalPages').then(module => ({ default: module.PortalVerifyLanding })));
const PortalAccessRequestPage = lazyWithReload(() => import('./pages/portal/PortalAccessRequestPage'));
const PortalTaskPage = lazyWithReload(() => import('./pages/portal/PortalTaskPage'));
const UniversalLinkLanding = lazyWithReload(() => import('./pages/UniversalLinkLanding').then(module => ({ default: module.UniversalLinkLanding })));
// Alert email buttons land here: sign in if needed, then confirm (Eric, 2026-10-28).
const AlertActionPage = lazyWithReload(() => import('./pages/AlertActionPage'));

// Records pages (internal-only)
const RecordsDashboard = lazyWithReload(() => import('./pages/records/Dashboard').then(module => ({ default: module.RecordsDashboard })));
const RecordsInventory = lazyWithReload(() => import('./pages/records/Inventory').then(module => ({ default: module.RecordsInventory })));
const RecordsFiles = lazyWithReload(() => import('./pages/records/Files').then(module => ({ default: module.RecordsFiles })));
const RecordsFilesImport = lazyWithReload(() => import('./pages/records/FilesImport').then(module => ({ default: module.RecordsFilesImport })));
const RecordsFilesImportReview = lazyWithReload(() => import('./pages/records/FilesImportReview').then(module => ({ default: module.RecordsFilesImportReview })));
const RecordsMaintenance = lazyWithReload(() => import('./pages/records/Maintenance').then(module => ({ default: module.RecordsMaintenance })));
const RecordsWorkOrders = lazyWithReload(() => import('./pages/records/WorkOrders').then(module => ({ default: module.RecordsWorkOrders })));
const RecordsDamage = lazyWithReload(() => import('./pages/records/Damage').then(module => ({ default: module.RecordsDamage })));
const SettingsFloorplans = lazyWithReload(() => import('./pages/settings/Floorplans').then(module => ({ default: module.SettingsFloorplans })));
const RecordsCheckIns = lazyWithReload(() => import('./pages/records/CheckIns').then(module => ({ default: module.RecordsCheckIns })));
const RecordsDevices = lazyWithReload(() => import('./pages/records/Devices').then(module => ({ default: module.RecordsDevices })));
// Alerts is a STANDALONE top-level page (not nested under /records).
const AlertsPage = lazyWithReload(() => import('./pages/Alerts').then(module => ({ default: module.AlertsPage })));
const RecordsEvents = lazyWithReload(() => import('./pages/records/Events').then(module => ({ default: module.RecordsEvents })));
const UnitRecords = lazyWithReload(() => import('./pages/records/UnitRecords').then(module => ({ default: module.UnitRecords })));

// Default home page component - redirects based on system config
function DefaultHomePage() {
  const { systemConfig } = useSystemConfig();
  const navigate = useNavigate();

  useEffect(() => {
    if (systemConfig.defaultHomePage === 'map') {
      navigate('/map', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [systemConfig.defaultHomePage, navigate]);

  // Show loading while redirecting
  return <LottieLoading message="Loading..." />;
}

// Flush cache page - clears all filter cache and redirects to home
function FlushPage() {
  const navigate = useNavigate();
  const { flushCache } = useFilters();

  useEffect(() => {
    console.log('🗑️ [FlushPage] Flushing all caches...');
    // flushCache() now clears both localStorage filter keys AND IndexedDB cache
    flushCache();
    console.log('✅ [FlushPage] Cache flushed, redirecting...');
    // Small delay to ensure cache is cleared, then redirect
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 100);
  }, [flushCache, navigate]);

  return <LottieLoading message="Flushing cache..." />;
}

// Logout page - forces user sign out and redirects to login
// Uses hard reload (window.location.href) instead of SPA navigation to fully clear
// all React state and contexts, preventing stale data if a different user logs in
function LogoutPage() {
  const { signOut } = useAuth();

  useEffect(() => {
    let hasRun = false;
    const performLogout = async () => {
      // Prevent multiple runs if effect re-triggers
      if (hasRun) return;
      hasRun = true;

      console.log('🚪 [LogoutPage] Signing out...');
      await signOut();
      console.log('✅ [LogoutPage] Signed out, redirecting to login...');
      // Hard reload to fully clear all React state/contexts
      window.location.href = '/login';
    };
    performLogout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount only

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="h-8 w-8 border-2 border-[#8FB560] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Signing out...</p>
      </div>
    </div>
  );
}

// Clear page - clears ALL caches AND logs out user
// Combines FlushPage + LogoutPage for complete session reset
// Works even when user is not authenticated or in a corrupted state
function ClearPage() {
  const { signOut, user } = useAuth();

  useEffect(() => {
    let hasRun = false;
    const performClear = async () => {
      if (hasRun) return;
      hasRun = true;

      console.log('🧹 [ClearPage] Starting complete cache clear and logout...');

      // Step 1: Clear all localStorage filter/settings caches
      try {
        const keysToRemove = Object.keys(localStorage).filter(key =>
          key.startsWith('company_filter_') ||
          key.startsWith('settings_filters_') ||
          key.startsWith('reports_') ||
          key.startsWith('ht_') ||
          key.includes('_cache')
        );
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('[ClearPage] localStorage cleared:', keysToRemove.length, 'keys');
      } catch (error) {
        console.warn('[ClearPage] Failed to clear localStorage:', error);
      }

      // Step 2: Clear IndexedDB cache
      try {
        const databases = await indexedDB.databases?.() || [];
        for (const db of databases) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
          }
        }
        console.log('[ClearPage] IndexedDB cleared');
      } catch (error) {
        console.warn('[ClearPage] Failed to clear IndexedDB:', error);
      }

      // Step 3: Sign out (if user is authenticated)
      if (user) {
        try {
          await signOut();
          console.log('[ClearPage] User signed out');
        } catch (error) {
          console.warn('[ClearPage] Failed to sign out:', error);
        }
      }

      console.log('✅ [ClearPage] Complete, redirecting to login...');
      // Hard reload to fully clear React state
      window.location.href = '/login';
    };

    performClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount

  return <LottieLoading message="Clearing..." />;
}

// Delay toast notifications until after the app's loading screens are dismissed
function DelayedToastContainer() {
  const { rbacReady } = useRBAC();
  const { isLoading: filtersLoading } = useFilters();
  const [showToasts, setShowToasts] = useState(false);

  useEffect(() => {
    // Wait for BOTH RBAC and filter data to finish loading
    const allLoaded = rbacReady && !filtersLoading;

    if (allLoaded && !showToasts) {
      // Wait for: React render cycle (~500ms) + LottieLoading fade (~500ms) + user orientation (~1000ms)
      const timer = setTimeout(() => setShowToasts(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [rbacReady, filtersLoading, showToasts]);

  if (!showToasts) return null;

  return (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="dark"
      limit={5}
    />
  );
}

/**
 * /records/:unitCode/compliance → that unit's Files tab.
 *
 * A relative <Navigate to="../files"> pops the WHOLE two-segment route and
 * lands on the library instead of the unit, so the param has to be read.
 */
function UnitComplianceRedirect() {
  const { unitCode } = useParams();
  return <Navigate to={`/records/${unitCode}/files`} replace />;
}

function App() {
  return (
    <DarkModeProvider>
      <LiveDemoProvider>
        <UserLocationProvider>
          <BrowserRouter>
            <AuthProvider>
              <UnitStatusProvider>
              <RBACProvider>
          <SuperadminNotificationProvider>
          <FilterProvider>
            <MapProvider>
              <SettingsProvider>
              <PostHogProvider>
              <Suspense fallback={<LottieLoading message="Loading..." />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  {/* Public invitation acceptance route */}
                  <Route path="/accept-invite" element={<AcceptInvite />} />
                  {/* Flush cache route */}
                  <Route path="/flush" element={<FlushPage />} />
                  {/* Logout route - forces sign out */}
                  <Route path="/logout" element={<LogoutPage />} />
                  {/* Clear route - flushes ALL caches AND logs out */}
                  <Route path="/clear" element={<ClearPage />} />
                  {/* Public check-in/out route (no auth required) */}
                  <Route path="/check-in/:token" element={<CheckInOutPage />} />
                  {/* T-144 PR#3b: public document-share capability link (no auth required) */}
                  <Route path="/share/:token" element={<DocumentSharePage />} />
                  {/* T-036: Universal Link landing for /u/:unitCode. Public.
                      iOS-with-Pocket intercepts before this renders via the
                      AASA file at /.well-known/apple-app-site-association.
                      Other devices see a generic CTA — same for all
                      unitCode values, no API call (info-disclosure guard). */}
                  <Route path="/u/:unitCode" element={<UniversalLinkLanding />} />
                  {/* Alert email actions: the page signs the person in and back itself
                      (/login?next=), so it is not wrapped in ProtectedRoute. */}
                  <Route path="/alert-action" element={<AlertActionPage />} />
                  {/* Client Document Portal — share-link mode is public (cookie-gated API) */}
                  <Route path="/portal/link" element={<PortalLinkLanding />} />
                  <Route path="/portal/shared" element={<PortalSharedPage />} />
                  <Route path="/portal/verify" element={<PortalVerifyLanding />} />
                  {/* T-207: "Add as a contact" from a request-for-access email; signs in and back itself. */}
                  <Route path="/portal/access-request" element={<PortalAccessRequestPage />} />
                  {/* T-220: "please upload this" for a broker; no sign-in, the link is the key. */}
                  <Route path="/portal/task" element={<PortalTaskPage />} />
                  <Route
                    path="/portal"
                    element={
                      <ProtectedRoute>
                        <PortalAccountPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/hire/*" element={<ProtectedRoute><HirePage /></ProtectedRoute>} />
                  {/* Default home page - redirects based on system config */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <DefaultHomePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  {/* Map View */}
                  <Route
                    path="/map"
                    element={
                      <ProtectedRoute>
                        <MapPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Reports Page */}
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  {/* Alerts — standalone top-level page (internal-only). */}
                  <Route
                    path="/alerts"
                    element={
                      <InternalRoute>
                        <AlertsPage />
                      </InternalRoute>
                    }
                  />
                  <Route
                    path="/units/:unitId/gallery"
                    element={
                      <ProtectedRoute>
                        <GalleryPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Settings Routes (formerly Admin) */}
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <SettingsLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<SettingsOverview />} />
                    <Route path="profile" element={<SettingsProfile />} />
                    <Route path="notifications" element={
                      <AdminRoute>
                        <SettingsNotifications />
                      </AdminRoute>
                    } />
                    {/* Messages and Invoices - TBD, hidden for now */}
                    {/* <Route path="messages" element={
                      <AdminRoute>
                        <SettingsMessages />
                      </AdminRoute>
                    } />
                    <Route path="invoices" element={
                      <AdminRoute>
                        <SettingsInvoices />
                      </AdminRoute>
                    } /> */}
                    <Route path="groups" element={<SettingsGroups />} />
                    <Route path="units" element={<SettingsUnits />} />
                    <Route path="audit" element={<SettingsAuditLogs />} />
                    <Route path="company" element={<SettingsCompany />} />
                    <Route path="companies" element={<AdminCompanies />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="global-audit" element={<SettingsGlobalAudit />} />
                    <Route path="filters" element={<SettingsFilters />} />
                    <Route path="config" element={<SettingsConfig />} />
                    <Route path="alerts" element={<AlertsSettings />} />
                    <Route path="report-subscriptions" element={<ReportSubscriptionsInventory />} />
            <Route path="unbilled-deployments" element={<UnbilledDeployments />} />
                    <Route path="training" element={<TrainingSettings />} />
                    <Route path="ai" element={<AiSettings />} />
                    <Route path="client-portal" element={<ClientPortalSettings />} />
                    <Route path="rates" element={<ElectricRates />} />
                    <Route path="document-types" element={<DocumentTypes />} />
                    {/* T-071: moved from Records; stays internal-only as it was there. */}
                    <Route path="floorplans" element={
                      <InternalRoute>
                        <SettingsFloorplans />
                      </InternalRoute>
                    } />
                    <Route path="document-shares" element={<SettingsDocumentShares />} />
                    <Route path="devices" element={
                      <AdminRoute>
                        <SystemDevices />
                      </AdminRoute>
                    } />
                    {/* T-038: redirect any /settings/check-in* URL to /records/check-ins.
                        Four variants cover: hyphenated bare path + sub-paths, and
                        no-hyphen variants for older bookmarks. Query strings are
                        preserved by react-router but Navigate strips them — that's
                        fine for a deprecated entry point. */}
                    <Route path="check-in" element={<Navigate to="/records/check-ins" replace />} />
                    <Route path="check-in/*" element={<Navigate to="/records/check-ins" replace />} />
                    <Route path="checkin" element={<Navigate to="/records/check-ins" replace />} />
                    <Route path="checkin/*" element={<Navigate to="/records/check-ins" replace />} />
                    <Route path="qr-codes" element={
                      <AdminRoute>
                        <SettingsQRCodes />
                      </AdminRoute>
                    } />
                    {/* T-046: Pocket settings — admin-tier read-only ops surface for the
                        QR/Universal-Link pipeline. Future T-047/T-048/T-049 add tabs. */}
                    <Route path="pocket" element={
                      <AdminRoute>
                        <SettingsPocket />
                      </AdminRoute>
                    } />
                  </Route>
                  {/* Training Routes — CA CDL Class A trainer.
                      Open to every signed-in App user (spec §Access); progress
                      is private per user. TR-2 lands the shell; the study
                      screens (home/roadmap/learn/cards) arrive in TR-3 and the
                      test screens (practice/exam/review) in TR-4, each swapping
                      its placeholder element for the real page. */}
                  <Route
                    path="/training"
                    element={
                      <ProtectedRoute>
                        <TrainingLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<TrainingHome />} />
                    <Route path="roadmap" element={<TrainingRoadmap />} />
                    <Route path="learn" element={<TrainingLessons />} />
                    <Route path="learn/:lessonId" element={<TrainingLessonDetail />} />
                    <Route path="cards" element={<TrainingFlashcards />} />
                    <Route path="practice" element={<TrainingPractice />} />
                    <Route path="exam" element={<TrainingExam />} />
                    <Route path="review" element={<TrainingReview />} />
                  </Route>
                  {/* Records Routes (internal-only) */}
                  <Route
                    path="/records"
                    element={
                      <InternalRoute>
                        <RecordsLayout />
                      </InternalRoute>
                    }
                  >
                    <Route index element={<RecordsDashboard />} />
                    <Route path="inventory" element={<RecordsInventory />} />
                    {/* Compliance is retired; FILES replaces it (T-008). */}
                    <Route path="compliance" element={<Navigate to="/records/files" replace />} />
                    <Route path="files" element={<RecordsFiles />} />
                    {/* T-238 importer: bulk upload; review and filing come next. */}
                    <Route path="files/import" element={<RecordsFilesImport />} />
                    <Route path="files/import/review" element={<RecordsFilesImportReview />} />
                    <Route path="maintenance" element={<RecordsMaintenance />} />
                    <Route path="work-orders" element={<RecordsWorkOrders />} />
                    <Route path="damage" element={<RecordsDamage />} />
                    {/* Floorplans moved to Settings → Platform (T-071). */}
                    <Route path="floorplans" element={<Navigate to="/settings/floorplans" replace />} />
                    <Route path="check-ins" element={<RecordsCheckIns />} />
                    <Route path="devices" element={<RecordsDevices />} />
                    <Route path="events" element={<RecordsEvents />} />
                    {/* Alerts moved OUT of Records to a standalone top-level page. */}
                    <Route path="alerts" element={<Navigate to="/alerts" replace />} />
                    {/* Unit-specific routes (must be after static routes for correct matching) */}
                    <Route path=":unitCode" element={<UnitRecords />} />
                    <Route path=":unitCode/compliance" element={<UnitComplianceRedirect />} />
                    <Route path=":unitCode/:tab" element={<UnitRecords />} />
                  </Route>
                  {/* Redirect old admin routes */}
                  <Route path="/admin/*" element={<Navigate to="/settings" replace />} />
                </Routes>
              </Suspense>
              <DelayedToastContainer />
              </PostHogProvider>
              </SettingsProvider>
            </MapProvider>
          </FilterProvider>
              </SuperadminNotificationProvider>
              </RBACProvider>
              </UnitStatusProvider>
        </AuthProvider>
          </BrowserRouter>
        </UserLocationProvider>
      </LiveDemoProvider>
    </DarkModeProvider>
  );
}

export default App;
